export default class CostAndBudget {
    private element: Element;

    private readonly presentedBudgetInput: HTMLInputElement | null;
    private readonly completionBudgetInput: HTMLInputElement | null;
    private readonly estimatedCostInput: HTMLInputElement | null;
    private readonly estimatedCompletionCostInput: HTMLInputElement | null;
    private readonly manualCompletionBudgetCheckbox: HTMLInputElement | null;
    private readonly manualEstimatedCompletionCostCheckbox: HTMLInputElement | null;
    private readonly ongoingConstructionRatio: number;
    private readonly deviationElement: HTMLElement | null;

    public constructor(element: Element) {
        this.element = element;

        this.presentedBudgetInput = element.querySelector('input[name="presentedBudget"]');
        this.completionBudgetInput = element.querySelector('input[name="completionBudget"]');
        this.estimatedCostInput = element.querySelector('input[name="estimatedCost"]');
        this.estimatedCompletionCostInput = element.querySelector('input[name="estimatedCompletionCost"]');
        this.manualCompletionBudgetCheckbox = element.querySelector('input[name="manualCompletionBudget"]');
        this.manualEstimatedCompletionCostCheckbox = element.querySelector(
            'input[name="manualEstimatedCompletionCost"]',
        );
        this.deviationElement = element.querySelector('#deviation');

        const ongoingConstructionInput = element.querySelector(
            'input[name="ongoingConstructionRatio"]',
        ) as HTMLInputElement | null;
        this.ongoingConstructionRatio = parseFloat(ongoingConstructionInput!.value || '0');


        this.presentedBudgetInput!.addEventListener('input', () => this.updateCompletionBudget());
        this.estimatedCostInput!.addEventListener('input', () => this.updateEstimatedCompletionCost());

        this.manualCompletionBudgetCheckbox!.addEventListener('change', () => this.completionBudgetInputReadonly());
        this.manualEstimatedCompletionCostCheckbox!.addEventListener(
            'change',
            () => this.estimatedCompletionCostReadonly(),
        );

        this.completionBudgetInputReadonly();
        this.estimatedCompletionCostReadonly();
    }

    private updateCompletionBudget(): void {
        if (this.manualCompletionBudgetCheckbox === null || this.completionBudgetInput === null) {
            return;
        }

        if (this.manualCompletionBudgetCheckbox!.checked) {
            return;
        }

        const presentedBudget = parseFloat(this.presentedBudgetInput!.value) || 0;

        this.completionBudgetInput.value = (this.calculate(presentedBudget)).toString();

        this.updateDeviation();
    }

    private updateEstimatedCompletionCost(): void {
        if (this.manualEstimatedCompletionCostCheckbox === null || this.estimatedCompletionCostInput === null) {
            return;
        }

        if (this.manualEstimatedCompletionCostCheckbox!.checked) {
            return;
        }

        const completionCost = parseFloat(this.estimatedCostInput!.value) || 0;

        this.estimatedCompletionCostInput.value = (this.calculate(completionCost)).toString();

        this.updateDeviation();
    }

    private calculate(cost: number): number {
        return Math.round((cost * (1 - this.ongoingConstructionRatio)) / 1000) * 1000;
    }

    private completionBudgetInputReadonly(): void {
        if (this.manualCompletionBudgetCheckbox === null || this.completionBudgetInput === null) {
            return;
        }

        this.completionBudgetInput.readOnly = !this.manualCompletionBudgetCheckbox.checked;

        if (!this.manualCompletionBudgetCheckbox.checked) {
            this.updateCompletionBudget();
        }
    }

    private estimatedCompletionCostReadonly(): void {
        if (this.manualEstimatedCompletionCostCheckbox === null || this.estimatedCompletionCostInput === null) {
            return;
        }

        this.estimatedCompletionCostInput.readOnly = !this.manualEstimatedCompletionCostCheckbox.checked;

        if (!this.manualEstimatedCompletionCostCheckbox.checked) {
            this.updateEstimatedCompletionCost();
        }
    }

    private updateDeviation(): void {
        if (this.completionBudgetInput === null || this.estimatedCompletionCostInput === null) {
            this.setDeviationValue(0);

            return;
        }

        const completionBudget = parseFloat(this.completionBudgetInput.value) || 0;
        const completionCost = parseFloat(this.estimatedCompletionCostInput.value) || 0;

        if (completionBudget === 0 || completionCost === 0) {
            this.setDeviationValue(0);

            return;
        }

        const deviation = ((completionBudget - completionCost) / completionCost) * 100;

        this.setDeviationValue(deviation);
    }

    private setDeviationValue(value: number): void {
        this.deviationElement!.innerHTML = value.toFixed(1);

        this.deviationElement!.parentElement!.className = Math.abs(value) > 15 ? 'badge bg-danger' : 'badge bg-success';
    }
}
